import { useEffect, useRef, useState } from "react";
import "./Landing.css";

type Feature = { number: string; title: string; description: string; mediaLabel: string; mediaDescription: string; mediaSrc?: string };

const features: Feature[] = [
  { number: "01", title: "Bring every credit into one working file", description: "Start from your production data, organize names and roles into clear blocks, and keep the information ready for the next review.", mediaLabel: "Import and edit", mediaDescription: "A video showing production-file imports", mediaSrc: "/landing-workflow-import.mp4" },
  { number: "02", title: "Design credits for the way they will be seen", description: "Build a continuous Roller or a sequence of Cards, then shape the type, spacing, framing, and assets around each project.", mediaLabel: "Design workspace", mediaDescription: "Replace with a preview or formatting screenshot" },
  { number: "03", title: "Finish with a render your team can use", description: "Keep collaborators aligned, review the result in context, and export the finished credits in the format your delivery needs.", mediaLabel: "Review and render", mediaDescription: "Replace with a render queue or delivery screenshot" },
];

const faqs = [
  { question: "What can I create with FinTitles?", answer: "FinTitles supports continuous rolling credits and individually timed credit cards. You can build the credit content into formatted blocks, then preview and render the result for delivery." },
  { question: "Can I work with a team?", answer: "Yes. Projects can be shared with collaborators, with permissions that reflect what each person needs to edit or review." },
  { question: "Can I use my own fonts and logos?", answer: "Yes. Project assets include fonts and logos, so the credits can match the production’s established visual language." },
  { question: "Which formats can I render?", answer: "FinTitles can render image and video deliverables, including PDF, TIFF, PNG where available, H.264, H.265, and ProRes options." },
];

const Landing = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const pathLayerRef = useRef<SVGSVGElement>(null);
  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    const updateScrollState = () => setHasScrolled(window.scrollY > 8);
    updateScrollState();
    window.addEventListener("scroll", updateScrollState, { passive: true });
    return () => window.removeEventListener("scroll", updateScrollState);
  }, []);

  useEffect(() => {
    const landingContent = mainRef.current;
    const pathLayer = pathLayerRef.current;
    if (!landingContent || !pathLayer) return;

    const paths = Array.from(pathLayer.querySelectorAll<SVGPathElement>("path"));
    const pathLengths = paths.map((path) => path.getTotalLength());
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let animationFrame: number | null = null;
    let currentProgress = 0;
    let targetProgress = 0;

    const setPathProgress = (progress: number) => {
      paths.forEach((path, index) => {
        const length = pathLengths[index];
        path.style.strokeDasharray = `${length}`;
        path.style.strokeDashoffset = `${length * (1 - progress)}`;
      });
    };

    const updateTargetProgress = () => {
      const rect = landingContent.getBoundingClientRect();
      const pageTop = window.scrollY + rect.top;
      const scrollableDistance = Math.max(1, landingContent.offsetHeight - window.innerHeight + pageTop);
      targetProgress = Math.min(1, Math.max(0, (window.scrollY - pageTop) / scrollableDistance));

      if (animationFrame === null && !reducedMotion.matches) {
        animationFrame = window.requestAnimationFrame(animate);
      }
    };

    const animate = () => {
      currentProgress += (targetProgress - currentProgress) * 0.12;
      if (Math.abs(targetProgress - currentProgress) < 0.001) currentProgress = targetProgress;
      setPathProgress(currentProgress);

      animationFrame = currentProgress === targetProgress ? null : window.requestAnimationFrame(animate);
    };

    const handleMotionPreferenceChange = () => {
      if (reducedMotion.matches) {
        if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
        animationFrame = null;
        currentProgress = 1;
        targetProgress = 1;
        setPathProgress(1);
        return;
      }

      currentProgress = 0;
      setPathProgress(0);
      updateTargetProgress();
    };

    setPathProgress(0);
    handleMotionPreferenceChange();
    window.addEventListener("scroll", updateTargetProgress, { passive: true });
    window.addEventListener("resize", updateTargetProgress);
    reducedMotion.addEventListener("change", handleMotionPreferenceChange);

    return () => {
      window.removeEventListener("scroll", updateTargetProgress);
      window.removeEventListener("resize", updateTargetProgress);
      reducedMotion.removeEventListener("change", handleMotionPreferenceChange);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <div className="landing" id="home">
      <header className={`landing-nav${hasScrolled ? " is-scrolled" : ""}`}>
        <a className="landing-brand" href="#home" aria-label="FinTitles home" onClick={closeMenu}>
          <img className="landing-brand-mark" src="/shared/logos/Fintitles logo.png" alt="" />
          <span>FinTitles</span>
        </a>
        <button type="button" className="landing-menu-toggle" aria-expanded={isMenuOpen} aria-controls="landing-navigation" onClick={() => setIsMenuOpen((isOpen) => !isOpen)}>
          <span className="landing-menu-toggle-label">Menu</span>
          <span className="landing-menu-toggle-icon" aria-hidden="true"><span /><span /><span /></span>
        </button>
        <nav className={`landing-links${isMenuOpen ? " is-open" : ""}`} id="landing-navigation" aria-label="Main navigation">
          <a className="landing-nav-link" href="#home" onClick={closeMenu}>Home</a>
          <a className="landing-nav-link" href="#pricing" onClick={closeMenu}>Pricing</a>
          <a className="landing-nav-link" href="#faq" onClick={closeMenu}>FAQ</a>
          <a className="landing-nav-link" href="#about" onClick={closeMenu}>About</a>
          <a className="landing-sign-in" href="/login?destination=project-selector">Sign In</a>
        </nav>
      </header>

      <main className="landing-main" ref={mainRef}>
        <svg className="landing-path-layer" ref={pathLayerRef} viewBox="0 0 2003 3544" preserveAspectRatio="xMidYMin slice" fill="none" aria-hidden="true" focusable="false">
          <path d="M9.00029 9.00024C233.5 22.5002 264.427 187.737 217 553.5C184 808 562.889 939.5 592 874C615.111 822 562 772.5 501.5 800C425 834.773 455 926 501.5 939.5C548 953 629.154 940.682 693.5 926C945.5 868.5 1396.5 673.5 1624 843.5C1851.5 1013.5 1936 1413.5 1870.5 1635C1805 1856.5 1769 1922.5 1670.5 1941.5C1572 1960.5 981 1813 707 1870.5C368.319 1941.57 269 2012.5 184 2223.5C169.614 2259.21 -32.4997 2984 345.5 3096.5C637.733 3183.47 1906 3214.5 1993.5 3534.5" stroke="#EDEDED" strokeWidth="18" strokeLinecap="round" />
        </svg>
        <section className="landing-hero" aria-labelledby="landing-hero-title" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url("/landing-hero.jpeg")' }}>
          <div className="landing-hero-content">
            <h1 className="landing-hero-title" id="landing-hero-title">End Titles Made Effortless</h1>
            <p className="landing-hero-description">FinTitles turns production credit data into clean, professional end titles. Import, organize, design, and render in one focused workspace.</p>
            <a className="landing-hero-cta" href="/login">Start a project</a>
          </div>
        </section>

        <section className="landing-section landing-about" id="about" aria-labelledby="about-title">
          <div className="landing-section-heading"><p className="landing-eyebrow">About FinTitles</p><h2 id="about-title">A clearer path from credits list to final frame.</h2></div>
          <div className="landing-about-copy"><p>FinTitles gives production teams a dedicated place to prepare end titles without losing track of the details that matter: names, roles, layout, timing, and delivery.</p><p>Use the workflow that fits the project. Create a traditional rolling sequence, or build individual cards with their own timing and framing.</p></div>
        </section>

        <section className="landing-section landing-features" aria-labelledby="features-title">
          <div className="landing-section-heading"><p className="landing-eyebrow">The workflow</p><h2 id="features-title">Made for the handoff points that usually slow credits down.</h2></div>
          <div className="landing-feature-list">
            {features.map((feature, index) => (
              <article className={`landing-feature landing-feature--${index % 2 === 0 ? "left" : "right"}`} key={feature.number}>
                <div className="landing-feature-copy"><span className="landing-feature-number">{feature.number}</span><h3>{feature.title}</h3><p>{feature.description}</p></div>
                {feature.mediaSrc ? (
                  <video className="landing-media-video" autoPlay loop muted playsInline aria-label={feature.mediaDescription}>
                    <source src={feature.mediaSrc} type="video/mp4" />
                  </video>
                ) : (
                  <div className="landing-media-slot" role="img" aria-label={feature.mediaDescription}><span className="landing-media-slot-label">{feature.mediaLabel}</span><span className="landing-media-slot-description">{feature.mediaDescription}</span></div>
                )}
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section landing-pricing" id="pricing" aria-labelledby="pricing-title">
          <div className="landing-pricing-card"><div><p className="landing-eyebrow">Pricing</p><h2 id="pricing-title">A plan shaped around your production.</h2><p>Tell us about your workflow, team, and delivery needs. We’ll help you find the right setup for FinTitles.</p></div><a className="landing-outline-cta" href="mailto:support@fintitles.com?subject=FinTitles%20pricing">Contact for pricing</a></div>
        </section>

        <section className="landing-section landing-faq" id="faq" aria-labelledby="faq-title">
          <div className="landing-section-heading"><h2 id="faq-title">FAQs</h2></div>
          <div className="landing-faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return <article className="landing-faq-item" key={faq.question}><h3><button type="button" className="landing-faq-question" aria-expanded={isOpen} aria-controls={`landing-faq-answer-${index}`} onClick={() => setOpenFaqIndex(isOpen ? null : index)}><span>{faq.question}</span><span className="landing-faq-icon" aria-hidden="true">{isOpen ? "−" : "+"}</span></button></h3><div className={`landing-faq-answer${isOpen ? " is-open" : ""}`} id={`landing-faq-answer-${index}`} aria-hidden={!isOpen}><div className="landing-faq-answer-content"><p>{faq.answer}</p></div></div></article>;
            })}
          </div>
        </section>

        <section className="landing-closing" aria-labelledby="closing-title"><p className="landing-eyebrow">Ready when you are</p><h2 id="closing-title">Give the final credits the same care as the final cut.</h2><a className="landing-hero-cta" href="/login?destination=project-selector">Sign in to FinTitles</a></section>
      </main>
      <footer className="landing-footer"><a className="landing-brand" href="#home" aria-label="Back to FinTitles home"><img className="landing-brand-mark" src="/shared/logos/Fintitles logo.png" alt="" /><span>FinTitles</span></a><a href="mailto:support@fintitles.com">support@fintitles.com</a></footer>
    </div>
  );
};

export default Landing;
